import { useEffect, useRef, useState } from "react";
import { BarChart, ChevronDown, HelpCircle, Key, LogOut, Menu, Search, User, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ebidImage from "../../assets/images/logo.svg";
import { useTheme } from "../../theme/ThemeProvider";
import authService from '../../services/authService';
import { ROUTES } from '../../routes/routePaths';
import { FaStar } from "react-icons/fa";
import TutorialVideoModal from '../ui/TutorialVideoModal';

const SecondaryHeader = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const profileRef = useRef(null);
    const menuRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                profileRef.current &&
                !profileRef.current.contains(event.target) &&
                (menuRef.current === null || !menuRef.current.contains(event.target)) &&
                (mobileMenuRef.current === null || !mobileMenuRef.current.contains(event.target))
            ) {
                setIsProfileOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleLogoClick = (e) => {
        e.preventDefault();
        navigate('/dashboard');
    };

    return (
        <header className={`sticky top-0 z-40 w-full border-b transition-all duration-300 ${scrolled ? 'bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-md' : 'bg-white dark:bg-gray-800'
            }`}>
            <div className="container flex h-16 items-center px-4 mx-auto">
                <div className="flex items-center space-x-3">
                    <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2">
                        <img src={ebidImage} alt="Logo" className="h-11 object-contain" />
                    </Link>
                </div>

                <div className="ml-auto flex items-center space-x-4">
                    {/* Advanced Search Link */}
                    <Link
                        to={ROUTES.ADVANCED_SEARCH_LIVE}
                        className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            location.pathname === ROUTES.ADVANCED_SEARCH_LIVE || location.pathname === ROUTES.ADVANCED_SEARCH
                            ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-gray-700'
                            }`}
                    >
                        <Search className="w-5 h-5" />
                        <span>Advanced Search</span>
                    </Link>

                    <Link
                        to={ROUTES.FAVOURITE_TENDERS}
                        className={`flex   items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${location.pathname === '/favourite-tenders'
                            ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-gray-700'
                            }`}
                    >
                        <FaStar className="w-5 h-5" />
                        <span>Favourite Tenders</span>
                    </Link>
                    {/* Dashboard Link */}
                    <div className="hidden md:block">
                        <Link
                            to={ROUTES.DASHBOARD}
                            className={`flex   items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${location.pathname === '/dashboard'
                                ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                                : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-gray-700'
                                }`}
                        >
                            <BarChart className="w-5 h-5" />
                            <span>Dashboard</span>
                        </Link>
                    </div>

                    {/* Help Button */}
                    <button
                        onClick={() => setIsHelpModalOpen(true)}
                        className="hidden md:flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-gray-700"
                        title="Watch tutorial video"
                    >
                        <HelpCircle className="w-5 h-5" />
                        <span>Help</span>
                    </button>


                    {/* Theme Toggle */}
                    {/* <button
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={toggleTheme}
                    >
                        {theme === 'dark' ? <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" /> : <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />}
                    </button> */}

                    {/* Profile Dropdown */}
                    <div className="md:block hidden relative" ref={profileRef}>
                        <button
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            onClick={() => setIsProfileOpen(!isProfileOpen)}
                        >
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Profile</span>
                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isProfileOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                                <div className="p-2">
                                    <Link
                                        to={ROUTES.PROFILE}
                                        className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <User className="w-5 h-5 text-blue-500" />
                                        <span>My Profile</span>
                                    </Link>

                                    <Link
                                        to={ROUTES.CHANGE_PASSWORD}
                                        className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                        onClick={() => setIsProfileOpen(false)}
                                    >
                                        <Key className="w-5 h-5 text-blue-500" />
                                        <span>Change Password</span>
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setIsHelpModalOpen(true);
                                            setIsProfileOpen(false);
                                        }}
                                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                    >
                                        <HelpCircle className="w-5 h-5 text-blue-500" />
                                        <span>Tutorial Video</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            authService.logout();
                                            setIsProfileOpen(false);
                                        }}
                                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                        <LogOut className="w-5 h-5" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        {isOpen ? <X className="h-5 w-5 text-gray-600 dark:text-gray-400" /> : <Menu className="h-5 w-5 text-gray-600 dark:text-gray-400" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div ref={mobileMenuRef} className="md:hidden bg-white dark:bg-gray-800 border-t dark:border-gray-700">
                    <div className="px-4 py-3 space-y-2">
                        <Link
                            to={ROUTES.ADVANCED_SEARCH_LIVE}
                            className={`flex items-center space-x-3 px-4 py-2 text-sm rounded-lg transition-colors ${
                                location.pathname === ROUTES.ADVANCED_SEARCH_LIVE || location.pathname === ROUTES.ADVANCED_SEARCH
                                ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            onClick={() => setIsOpen(false)}
                        >
                            <Search className="w-5 h-5 text-blue-500" />
                            <span>Advanced Search</span>
                        </Link>
                        <Link
                            to={ROUTES.PROFILE}
                            className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <User className="w-5 h-5 text-blue-500" />
                            <span>My Profile</span>
                        </Link>
                        <div className="block md:hidden">
                            <Link
                                to={ROUTES.DASHBOARD}
                                className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                onClick={() => setIsOpen(false)}
                            >
                                <BarChart className="w-5 h-5 text-blue-500" />
                                <span>Dashboard</span>
                            </Link>
                        </div>
                        <Link
                            to={ROUTES.CHANGE_PASSWORD}
                            className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            onClick={() => setIsOpen(false)}
                        >
                            <Key className="w-5 h-5 text-blue-500" />
                            <span>Change Password</span>
                        </Link>
                        <button
                            onClick={() => {
                                setIsHelpModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <HelpCircle className="w-5 h-5 text-blue-500" />
                            <span>Tutorial Video</span>
                        </button>
                        <button
                            onClick={() => {
                                authService.logout();
                                setIsOpen(false);
                            }}
                            className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                            <LogOut className="w-5 h-5" />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Tutorial Video Modal */}
            <TutorialVideoModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
        </header>
    );
}

export default SecondaryHeader;